import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Crown, MessageSquare, Bot, TrendingUp, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import AppSidebar from '../components/AppSidebar';
import { api } from '../lib/api';
import { toast } from 'sonner';

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-2xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between text-muted-foreground"><span className="text-xs font-semibold uppercase tracking-wider">{label}</span><Icon className="h-4 w-4" /></div>
    <p className="mt-3 font-display text-3xl font-bold">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const Admin = () => {
  const { t, lang } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const a = t.admin;

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/chat'); return; }
    api.adminStats().then(setStats).catch((e) => toast.error(e.message));
    // eslint-disable-next-line
  }, []);

  const max = stats ? Math.max(1, ...stats.series.map((s) => s.messages)) : 1;
  const fmtDate = (d) => new Date(d).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: '2-digit', month: 'short' });

  return (
    <div className="flex bg-background" data-testid="admin-page">
      <AppSidebar conversations={[]} onNewChat={() => navigate('/chat')} />
      <main className="flex-1 h-screen overflow-y-auto min-w-0">
        <div className="h-16 border-b border-border flex items-center px-5 sm:px-8 sticky top-0 bg-background/80 backdrop-blur z-10">
          <h1 className="font-display text-xl font-bold">{a.title}</h1>
        </div>
        {!stats ? <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 space-y-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Users} label={a.users} value={stats.users_total} sub={`+${stats.users_today} ${a.today}`} testid="stat-users" />
              <Stat icon={Crown} label={a.pro} value={stats.users_pro} sub={`MRR ≈ ${stats.mrr.toFixed(2)} €`} testid="stat-pro" />
              <Stat icon={MessageSquare} label={a.messages} value={stats.messages_total} sub={`${stats.messages_today} ${a.today}`} testid="stat-messages" />
              <Stat icon={Bot} label={a.agents} value={stats.agents_custom} sub={`${stats.conversations_total} ${a.conversations}`} testid="stat-agents" />
            </div>

            <div className="rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> {a.activity}</h2>
              <div className="mt-6 flex items-end gap-1.5 h-40" data-testid="activity-chart">
                {stats.series.map((s) => (
                  <div key={s.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${fmtDate(s.date)} · ${s.messages} msgs · ${s.signups} inscrits`}>
                    <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">{s.messages}</span>
                    <div className="w-full rounded-t-md bg-primary/80 group-hover:bg-primary transition-colors" style={{ height: `${Math.max(3, (s.messages / max) * 120)}px` }} />
                    <span className="text-[10px] text-muted-foreground hidden sm:block">{fmtDate(s.date).split(' ')[0]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="px-6 py-4 border-b border-border"><h2 className="font-display font-semibold">{a.recent}</h2></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="users-table">
                  <thead className="text-xs text-muted-foreground uppercase tracking-wider bg-secondary/50">
                    <tr><th className="text-left px-6 py-3 font-semibold">{a.user}</th><th className="text-left px-4 py-3 font-semibold">{a.plan}</th><th className="text-left px-4 py-3 font-semibold">{a.provider}</th><th className="text-left px-4 py-3 font-semibold">{a.since}</th></tr>
                  </thead>
                  <tbody>
                    {stats.recent_users.map((u) => (
                      <tr key={u.user_id} className="border-t border-border hover:bg-accent/40">
                        <td className="px-6 py-3"><p className="font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></td>
                        <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded ${u.plan === 'pro' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>{u.plan === 'pro' ? 'PRO' : t.account.free}</span>{u.role === 'admin' && <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded bg-foreground text-background">ADMIN</span>}</td>
                        <td className="px-4 py-3 text-muted-foreground capitalize">{u.provider}</td>
                        <td className="px-4 py-3 text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
